<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.videoProducer.hero.title')"
      :paragraph="t('jc.view.videoProducer.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.videoProducer.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.videoProducer.hero.float.alt'),
      }"
      :label="t('jc.view.videoProducer.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.videoProducer.ourOffer.title')"
      :paragraph="t('jc.view.videoProducer.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.videoProducer.ourConditions.title')"
      :paragraph="t('jc.view.videoProducer.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.videoProducer.yourTasks.title')"
      :paragraph="t('jc.view.videoProducer.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.videoProducer.impressions.title')"
      :paragraph="t('jc.view.videoProducer.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.videoProducer.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.videoProducer.howToApply.title')"
      :paragraph="t('jc.view.videoProducer.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.videoProducer.moreInformation.title')"
        :paragraph="t('jc.view.videoProducer.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/video_producer.png'

definePageMeta({
  nameKey: 'participate-videoProducer',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.videoProducer.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'dragon',
    text: t('jc.view.videoProducer.ourOffer.iconHighlight.creativeDevelopment.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.videoProducer.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: 'server',
    text: t('jc.view.videoProducer.ourOffer.iconHighlight.serverResources.text'),
  },
  {
    icon: 'cubes',
    text: t('jc.view.videoProducer.ourOffer.iconHighlight.replaymod.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.videoProducer.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.videoProducer.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.videoProducer.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.videoProducer.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.videoProducer.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.videoProducer.ourOffer.icon.teamTools.text'),
  },
  {
    icon: 'image-portrait',
    text: t('jc.view.videoProducer.ourOffer.icon.portfolio.text'),
  },
  {
    icon: 'file-certificate',
    text: t('jc.view.videoProducer.ourOffer.icon.licences.text'),
  },
  {
    icon: 'fingerprint',
    text: t('jc.view.videoProducer.ourOffer.icon.corporateIdentity.text'),
  },
  {
    icon: 'gears',
    text: t('jc.view.videoProducer.ourOffer.icon.supportedWorkflows.text'),
  },
  {
    icon: 'comments',
    text: t('jc.view.videoProducer.ourOffer.icon.exchange.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.videoProducer.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'alicorn',
    text: t('jc.view.videoProducer.ourConditions.icon.fantasy.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.videoProducer.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.videoProducer.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.videoProducer.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.videoProducer.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'scissors',
    text: t('jc.view.videoProducer.yourTasks.icon.transform.text'),
  },
  {
    icon: 'camera-movie',
    text: t('jc.view.videoProducer.yourTasks.icon.capture.text'),
  },
  {
    icon: 'microphone',
    text: t('jc.view.videoProducer.yourTasks.icon.narrate.text'),
  },
  {
    icon: 'scroll',
    text: t('jc.view.videoProducer.yourTasks.icon.design.text'),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.videoProducer.yourTasks.icon.update.text'),
  },
  {
    icon: 'person-walking',
    text: t('jc.view.videoProducer.yourTasks.icon.breathe.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'timelapse',
    mediaType: 'video/*',
    src: '/images/impressions/video_producer/timelapse.mp4',
    options: {
      controls: true,
      muted: true,
      autoplay: true,
    },
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.videoProducer.howToApply.icon.firstContact.title'),
    text: t('jc.view.videoProducer.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.videoProducer.howToApply.icon.conversation.title'),
    text: t('jc.view.videoProducer.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.videoProducer.howToApply.icon.wait.title'),
    text: t('jc.view.videoProducer.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.videoProducer.howToApply.icon.trainee.title'),
    text: t('jc.view.videoProducer.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.videoProducer.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.videoProducer.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.videoProducer.moreInformation.card.team.text'),
    label: t('jc.view.videoProducer.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.videoProducer.moreInformation.card.weekly_review.text'),
    label: t('jc.view.videoProducer.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.videoProducer.moreInformation.card.plan.text'),
    label: t('jc.view.videoProducer.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.videoProducer.moreInformation.card.stackShare.text'),
    label: t('jc.view.videoProducer.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.videoProducer.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.videoProducer.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.videoProducer.moreInformation.card.blog.text'),
    label: t('jc.view.videoProducer.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
